import React from 'react';
import { useTranslation } from 'react-i18next';
import { Site, SiteArea } from 'graphql-common';
import { ApolloError } from '@apollo/client/errors';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import { NavItem } from '@lib/components/Menu/Menu';
import useSearchParam from '@lib/hooks/useSearchParam';
import { Column } from '@lib/components/Table/types';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import SiteMenuBar from 'components/SiteMenuBar/SiteMenuBar';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { ListQueryHookResult } from 'utils/fetch/types';

interface Props {
  openSiteAreaCreateModal: () => void;
  siteAreaError?: ApolloError;
  siteData: Site;
  siteError?: ApolloError;
  siteId: string;
  siteLoading?: boolean;
  tableActionsForSelected?: React.ReactNode[];
  tableActionsMenu?: NavItem<SiteArea>[];
  tableColumns: Column<SiteArea>[];
  useBulkSelectResult: UseBulkSelectResult<SiteArea>;
  isViewOnly: boolean;
  siteAreasQueryHookResult: ListQueryHookResult<SiteArea>;
}

function SiteAreas(props: Props) {
  const {
    isViewOnly,
    openSiteAreaCreateModal,
    siteAreaError,
    siteAreasQueryHookResult,
    siteData,
    siteError,
    siteId,
    siteLoading,
    tableActionsForSelected,
    tableActionsMenu,
    tableColumns,
    useBulkSelectResult,
  } = props;
  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();

  if (siteLoading) {
    return <CircleLoader />;
  }

  if (siteAreasQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteAreasQueryHookResult.error)}
        description={siteError?.message}
      />
    );
  }

  if (siteAreaError) {
    return (
      <ErrorPage
        status={getStatusFromError(siteAreaError)}
        description={siteAreaError?.message}
      />
    );
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-arias-yet')}
      text={t('no-arias-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="add"
                buttonText={t('create-site-area')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={openSiteAreaCreateModal}
              />,
            ]
      }
    />
  );

  return (
    <PageBody withoutHeader>
      <SiteMenuBar
        id={siteId}
        siteAreasCount={siteData?.siteAreasCount}
        usersCount={siteData?.usersCount}
      />
      <DashboardContentMaxWidth>
        <WhiteBox>
          <DashboardTitleHeader
            title={siteData?.name}
            actions={
              isViewOnly ? (
                <ViewOnlyChip key="view" />
              ) : (
                <Button
                  key="add"
                  buttonText={t('create-new-site-area')}
                  buttonType={ButtonTypes.primaryFilled}
                  onClick={openSiteAreaCreateModal}
                  leftIcon="add"
                  leftIconClassName={materialSymbolsOutlined}
                />
              )
            }
          />
          {siteAreasQueryHookResult.firstLoading ? (
            <CircleLoader />
          ) : (
            <>
              {((!!siteAreasQueryHookResult.totalCount &&
                !!siteAreasQueryHookResult.collection.length) ||
                searchQuery) && (
                <Search placeholder={t('site-areas-search-placeholder')} />
              )}
              <Table
                actionsForSelected={tableActionsForSelected}
                actionsMenu={tableActionsMenu}
                columns={tableColumns}
                counterText={t('area-plural', {
                  count: siteAreasQueryHookResult.totalCount,
                })}
                data={siteAreasQueryHookResult.collection}
                emptyState={emptyState}
                id="siteAreas"
                loading={siteAreasQueryHookResult.loading}
                totalEntries={siteAreasQueryHookResult.totalCount}
                useBulkSelectResult={useBulkSelectResult}
              />
            </>
          )}
        </WhiteBox>
      </DashboardContentMaxWidth>
    </PageBody>
  );
}

export default SiteAreas;
